/**
 * Two guards on the way into an identity, shared by both clients (playtest one, 2026-08-08).
 *
 * The first real player left three guests behind in one session - "Guest", "Login", "Do it". Two of
 * those are not names anybody chose: they are what the player typed while trying to work out how to
 * drive the app, landing in the only text box on screen. Neither client looked at the string at all,
 * and neither asked whether the person already HAD an identity before minting another one.
 *
 * Both guards warn and then get out of the way. A name is the player's to pick, including a silly one,
 * and a refusal here would be a client deciding it knows better than the person naming themselves.
 */

/**
 * Words this app answers to, typed alone. Deliberately a CLOSED list of the app's own vocabulary
 * rather than a guess at what a name looks like: "Login" is a command somebody typed at the wrong box,
 * and "Do it" is not - it is prose, and no rule that catches it leaves real names alone.
 */
const COMMAND_WORDS = new Set([
  'login',
  'logout',
  'join',
  'leave',
  'create',
  'list',
  'decks',
  'help',
  'commands',
  'reload',
  'diag',
  'share',
  'start',
  'claim',
  'release',
]);

/**
 * True when a display name reads as an instruction to the app rather than as a person.
 *
 * A leading `/` is the unambiguous case - nobody is called `/claim` - and the closed word list catches
 * the one the playtest actually produced. Everything else is somebody's name and is left alone.
 */
export function nameReadsAsCommand(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return false;
  }

  return trimmed.startsWith('/') || COMMAND_WORDS.has(trimmed.toLowerCase());
}

/**
 * A two-step confirmation for the identity surfaces: the first attempt warns, an identical second
 * attempt goes through.
 *
 * Kept here rather than in either client because both need exactly this shape and the failure it
 * prevents is a client-side one - a modal dialog would be the alternative and neither client has one.
 * The pending value is the NAME, so changing your mind about the name also clears the confirmation:
 * "yes, really" has to mean yes to the thing you were warned about.
 */
export class DeliberateChoice {
  private pending: string | null = null;

  /**
   * Records an intent. Returns true when this is the second identical attempt, i.e. the player has
   * seen the warning and asked again anyway.
   */
  confirms(value: string): boolean {
    if (this.pending === value) {
      this.pending = null;
      return true;
    }

    this.pending = value;
    return false;
  }

  /** Forgets any half-made choice - after a success, or when the surface is left. */
  clear(): void {
    this.pending = null;
  }

  /** Whether a warning is currently outstanding, so a surface can say "again to confirm". */
  get outstanding(): boolean {
    return this.pending !== null;
  }
}
